import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SkeletonList } from '../components/Skeletons';
import { Bell, Loader2, Trash2, RefreshCw, Eye, Clock, CheckCheck, ArrowRightLeft, UserPlus, Building2, BellRing, DollarSign, Shield, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHANGE_ICONS = {
  status_change: ArrowRightLeft,
  party_change: Building2,
  new_candidacy: UserPlus,
  wealth_change: DollarSign,
};
const CHANGE_COLORS = {
  status_change: 'text-[#F57F17] bg-[#FFF8E1]',
  party_change: 'text-[#1E3A5F] bg-[#F4F6F8]',
  new_candidacy: 'text-[#2E7D32] bg-[#E8F5E9]',
  wealth_change: 'text-[#8C5A00] bg-[#FFF3E0]',
};
const SEVERITY_BADGE = {
  critical: 'bg-[#C62828] text-white',
  warning: 'bg-[#F57F17] text-white',
  info: 'bg-[#1565C0] text-white',
};

const formatBRL = (v) => {
  if (v == null || isNaN(v)) return String(v ?? '');
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
};

const Alerts = () => {
  const [items, setItems] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([loadWatchlist(), loadNotifications(), loadLastRun()]).finally(() => setLoading(false));
  }, []);

  const loadLastRun = async () => {
    try {
      const resp = await axios.get(`${API}/watchlist/last-run`, { timeout: 10000 });
      setLastRun(resp.data);
    } catch { /* */ }
  };

  const loadWatchlist = async () => {
    try {
      const resp = await axios.get(`${API}/watchlist`, { timeout: 15000 });
      setItems(resp.data.items || []);
    } catch { /* */ }
  };

  const loadNotifications = async () => {
    try {
      const resp = await axios.get(`${API}/notifications`, { timeout: 15000 });
      setNotifications(resp.data.notifications || []);
      setUnreadCount(resp.data.unread || 0);
    } catch { /* */ }
  };

  const handleCheckAll = async () => {
    setChecking(true);
    try {
      const resp = await axios.post(`${API}/watchlist/check`, {}, { timeout: 90000 });
      const totalChanges = (resp.data.results || []).reduce((sum, r) => sum + (r.changes?.length || 0), 0);
      if (totalChanges > 0) {
        toast.success(`${totalChanges} mudanca(s) detectada(s)!`);
      } else {
        toast.info('Nenhuma mudanca detectada');
      }
      await Promise.all([loadWatchlist(), loadNotifications(), loadLastRun()]);
    } catch {
      toast.error('Erro ao verificar TSE');
    } finally {
      setChecking(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { timeout: 10000 });
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('Todas marcadas como lidas');
    } catch {
      toast.error('Erro ao marcar como lidas');
    }
  };

  const handleMarkRead = async (notifId) => {
    try {
      await axios.post(`${API}/notifications/${notifId}/read`, {}, { timeout: 10000 });
      setNotifications(notifications.map(n => n.id === notifId ? { ...n, read: true } : n));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch { /* */ }
  };

  const handleDeleteNotif = async (notifId) => {
    try {
      await axios.delete(`${API}/notifications/${notifId}`, { timeout: 10000 });
      const removed = notifications.find(n => n.id === notifId);
      setNotifications(notifications.filter(n => n.id !== notifId));
      if (removed && !removed.read) setUnreadCount(Math.max(0, unreadCount - 1));
    } catch {
      toast.error('Erro ao remover');
    }
  };

  const handleRemoveWatch = async (itemId) => {
    try {
      await axios.delete(`${API}/watchlist/${itemId}`, { timeout: 10000 });
      setItems(items.filter(i => i.id !== itemId));
      toast.success('Removido do monitoramento');
    } catch {
      toast.error('Erro ao remover');
    }
  };

  const handleView = async (candidateId) => {
    try {
      const resp = await axios.get(`${API}/candidates/${candidateId}`, { timeout: 10000 });
      navigate('/', { state: { candidate: resp.data } });
    } catch {
      toast.error('Candidato nao encontrado');
    }
  };

  const formatDate = (d) => {
    if (!d) return 'N/A';
    try {
      return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return d; }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="text-center py-6 md:py-8">
        <h1 className="text-3xl sm:text-4xl font-black text-[#0F1C2E] tracking-tight mb-3" data-testid="alerts-title">
          Alertas e Monitoramento
        </h1>
        <p className="text-base text-[#475569]">
          Acompanhe atualizacoes dos candidatos monitorados no TSE
        </p>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Badge className="bg-[#1E3A5F] text-white">{items.length} monitorado{items.length !== 1 ? 's' : ''}</Badge>
          {unreadCount > 0 && (
            <Badge className="bg-[#C62828] text-white" data-testid="unread-count-badge">{unreadCount} nao lida{unreadCount !== 1 ? 's' : ''}</Badge>
          )}
        </div>
        <div className="flex gap-2">
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={handleMarkAllRead} data-testid="mark-all-read-btn" className="border-[#1E3A5F] text-[#1E3A5F]">
              <CheckCheck className="w-4 h-4 mr-1" /> Marcar todas lidas
            </Button>
          )}
          <Button size="sm" onClick={handleCheckAll} disabled={checking || items.length === 0} data-testid="check-all-btn" className="bg-[#1E3A5F] hover:bg-[#152B47] text-white">
            {checking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {checking ? 'Verificando...' : 'Verificar TSE'}
          </Button>
        </div>
      </div>

      {/* Varredura automática (banner informativo) */}
      <div className="flex items-start gap-2 p-3 bg-[#E8F5E9] border border-[#2E7D32]/30 rounded-md" data-testid="scheduler-banner">
        <Zap className="w-4 h-4 text-[#2E7D32] shrink-0 mt-0.5" />
        <div className="text-[11px] text-[#2E7D32] flex-1">
          <p className="font-semibold">Varredura automática diária · 03:00 UTC (00:00 BRT)</p>
          <p className="text-[#475569] mt-0.5">
            Monitoramos mudanças de status, partido, nova candidatura e <b>patrimônio declarado</b> no TSE.
            {lastRun?.last_run && (
              <> Última verificação: <b>{formatDate(lastRun.last_run)}</b>. {lastRun.checked} candidato(s) checados, {lastRun.total_changes || 0} mudança(s).</>
            )}
          </p>
        </div>
      </div>

      {loading ? <SkeletonList count={3} /> : (
        <Tabs defaultValue="notifications" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-white border border-[#E2E8F0] p-1 rounded-md">
            <TabsTrigger value="notifications" data-testid="tab-notifications" className="data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white font-medium">
              <BellRing className="w-4 h-4 mr-2" />
              Notificações {unreadCount > 0 && `(${unreadCount})`}
            </TabsTrigger>
            <TabsTrigger value="watchlist" data-testid="tab-watchlist" className="data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white font-medium">
              <Eye className="w-4 h-4 mr-2" />
              Monitorados ({items.length})
            </TabsTrigger>
          </TabsList>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="mt-4">
            <Card className="p-5 bg-white border border-[#E2E8F0] rounded-md">
              {notifications.length === 0 ? (
                <div className="text-center py-12" data-testid="notifications-empty">
                  <div className="w-16 h-16 bg-[#F4F6F8] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Bell className="w-8 h-8 text-[#94A3B8]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#0F1C2E] mb-2">Nenhuma Notificacao</h3>
                  <p className="text-sm text-[#475569] mb-4">
                    Adicione candidatos e clique "Verificar TSE" para detectar mudancas.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {notifications.map((notif) => {
                    const Icon = CHANGE_ICONS[notif.change_type] || ArrowRightLeft;
                    const colorClass = CHANGE_COLORS[notif.change_type] || 'text-[#475569] bg-[#F4F6F8]';
                    const isWealth = notif.change_type === 'wealth_change';
                    const sevClass = SEVERITY_BADGE[notif.severity] || SEVERITY_BADGE.info;

                    const fmtVal = (v) => {
                      if (v == null) return '';
                      if (isWealth && !isNaN(Number(v))) return formatBRL(Number(v));
                      return String(v);
                    };

                    return (
                      <div
                        key={notif.id}
                        data-testid={`notification-${notif.id}`}
                        className={`p-4 border rounded-md transition-all duration-200 ${notif.read ? 'border-[#E2E8F0] bg-white' : 'border-[#1E3A5F]/30 bg-[#F4F6F8]'}`}
                        onClick={() => !notif.read && handleMarkRead(notif.id)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${colorClass}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <p className="font-semibold text-[#0F1C2E] text-sm">{notif.candidate_name}</p>
                              {notif.severity && (
                                <Badge className={`text-[9px] px-1.5 py-0 ${sevClass}`} data-testid={`severity-${notif.severity}`}>
                                  {notif.severity === 'critical' ? 'CRÍTICO' : notif.severity === 'warning' ? 'ATENÇÃO' : 'INFO'}
                                </Badge>
                              )}
                              {!notif.read && <span className="w-2 h-2 rounded-full bg-[#C62828] shrink-0" />}
                            </div>
                            <p className="text-sm text-[#475569]">{notif.label}</p>
                            {notif.old_value != null && notif.new_value != null && (
                              <p className="text-xs text-[#94A3B8] mt-1">
                                <span className="line-through">{fmtVal(notif.old_value)}</span>
                                <span className="mx-2">→</span>
                                <span className="font-medium text-[#0F1C2E]">{fmtVal(notif.new_value)}</span>
                              </p>
                            )}
                            {notif.old_value == null && notif.new_value != null && (
                              <p className="text-xs mt-1">
                                <span className="font-medium text-[#2E7D32]">{fmtVal(notif.new_value)}</span>
                              </p>
                            )}
                            <p className="text-xs text-[#94A3B8] mt-1 flex items-center gap-2">
                              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(notif.created_at)}</span>
                              {notif.source && (
                                <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> {notif.source}</span>
                              )}
                            </p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteNotif(notif.id); }} className="text-[#94A3B8] hover:text-[#C62828] shrink-0">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Watchlist Tab */}
          <TabsContent value="watchlist" className="mt-4">
            <Card className="p-5 bg-white border border-[#E2E8F0] rounded-md">
              {items.length === 0 ? (
                <div className="text-center py-12" data-testid="alerts-empty">
                  <div className="w-16 h-16 bg-[#F4F6F8] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Eye className="w-8 h-8 text-[#94A3B8]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#0F1C2E] mb-2">Nenhum Candidato Monitorado</h3>
                  <p className="text-sm text-[#475569] mb-4">
                    Use o botao "Monitorar" no perfil de um candidato.
                  </p>
                  <Button onClick={() => navigate('/')} className="bg-[#1E3A5F] hover:bg-[#152B47] text-white" data-testid="go-search-btn">
                    Buscar Candidato
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((item) => (
                    <div key={item.id} className="p-4 border border-[#E2E8F0] rounded-md hover:border-[#1E3A5F] transition-all" data-testid={`watchlist-item-${item.id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-base font-bold text-[#0F1C2E]">{item.candidate_name}</h4>
                          <p className="text-xs text-[#94A3B8] mt-0.5">{item.candidate_state || 'N/A'}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            {item.tse_status && (
                              <Badge className={`text-xs ${
                                (item.tse_status || '').toLowerCase().includes('deferido') || (item.tse_status || '').toLowerCase().includes('eleito')
                                  ? 'bg-[#E8F5E9] text-[#2E7D32]' : item.tse_status === 'Nao encontrado' ? 'bg-[#F4F6F8] text-[#94A3B8]' : 'bg-[#FFF8E1] text-[#F57F17]'
                              }`}>TSE: {item.tse_status}</Badge>
                            )}
                            {item.alerts?.[0]?.cargo && (
                              <Badge variant="outline" className="text-xs border-[#1E3A5F] text-[#1E3A5F]">{item.alerts[0].cargo} ({item.alerts[0].year})</Badge>
                            )}
                            {item.alerts?.[0]?.partido && (
                              <Badge variant="outline" className="text-xs border-[#94A3B8] text-[#475569]">{item.alerts[0].partido}</Badge>
                            )}
                          </div>
                          <p className="text-xs text-[#94A3B8] mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {formatDate(item.last_checked)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleView(item.candidate_id)} className="text-[#1E3A5F] hover:bg-[#F4F6F8]" data-testid={`view-watchlist-${item.id}`}>
                            <Eye className="w-4 h-4 mr-1" /> Ver
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveWatch(item.id)} className="text-[#C62828] hover:bg-[#FFEBEE]" data-testid={`remove-watchlist-${item.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {checking && (
        <div className="p-3 bg-[#FFF8E1] border border-[#F57F17]/20 rounded-md flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-[#F57F17]" />
          <p className="text-xs text-[#F57F17]">Consultando TSE... Detectando mudancas de status, partido e novas candidaturas.</p>
        </div>
      )}
    </div>
  );
};

export default Alerts;
